// Groups catalyst events into month/year buckets for the company profile's
// expandable "Catalyst Events" section. Keeps the profile uncluttered while
// still exposing the full event history (one collapsible section per month).

export interface CatalystEventGroup {
  /** Sort key in YYYY-MM form ("0000-00" for undated, always sorts last). */
  key: string;
  /** Human label, e.g. "June 2026" (or "Undated"). */
  label: string;
  events: any[];
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Map a leading 3+ letter month token (jan, sept, december, ...) to 1-12.
const MONTH_TOKEN_TO_NUM: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

/**
 * Read the event date string from a catalyst event view document.
 * Tolerates both the wrapped (`value.date.value`) and flat (`value.date`) shapes.
 */
function readEventDate(event: any): string {
  const raw = event?.value?.date?.value ?? event?.value?.date;
  return typeof raw === "string" ? raw.trim() : "";
}

/**
 * Parse a year (and month when present) out of the many shapes catalyst event
 * dates take. Handles structured forms ("YYYY-MM-DD", "YYYY-MM", ISO
 * timestamps) and free-text forms ("Late 2024", "By 2032", "March 2026",
 * "Ongoing (primary completion dates 2027-2028)"). When a year exists anywhere
 * in the string we group by it (with a month if one is named); only strings
 * with no recognizable year fall through to "Undated".
 * Returns null when no year can be determined.
 */
function parseYearMonth(date: string): { year: number; month: number } | null {
  // Structured "YYYY-MM" (covers YYYY-MM-DD and ISO timestamps).
  const isoMatch = date.match(/\b(\d{4})-(\d{1,2})\b/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    return { year, month: month >= 1 && month <= 12 ? month : 0 };
  }

  // Otherwise, find the first plausible 4-digit year (19xx/20xx) anywhere.
  const yearMatch = date.match(/\b(19|20)\d{2}\b/);
  if (!yearMatch) return null;
  const year = Number(yearMatch[0]);

  // Pick up a named month if one appears (e.g. "March 2026", "Sept 2025").
  const monthMatch = date
    .toLowerCase()
    .match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/);
  const month = monthMatch ? MONTH_TOKEN_TO_NUM[monthMatch[1]] : 0;

  return { year, month };
}

/**
 * Group events by month/year, ordered newest-first. Input order within a
 * bucket is preserved (the backend already sorts events by date desc).
 */
export function groupCatalystEventsByMonth(
  events: any[],
): CatalystEventGroup[] {
  if (!Array.isArray(events)) return [];

  const buckets = new Map<string, CatalystEventGroup>();

  for (const event of events) {
    const parsed = parseYearMonth(readEventDate(event));

    let key: string;
    let label: string;
    if (!parsed) {
      key = "0000-00";
      label = "Undated";
    } else {
      const monthStr = String(parsed.month).padStart(2, "0");
      key = `${parsed.year}-${monthStr}`;
      label =
        parsed.month >= 1 && parsed.month <= 12
          ? `${MONTH_NAMES[parsed.month - 1]} ${parsed.year}`
          : `${parsed.year}`;
    }

    const existing = buckets.get(key);
    if (existing) {
      existing.events.push(event);
    } else {
      buckets.set(key, { key, label, events: [event] });
    }
  }

  // Newest first; "0000-00" (undated) naturally sorts last.
  return Array.from(buckets.values()).sort((a, b) =>
    a.key < b.key ? 1 : a.key > b.key ? -1 : 0,
  );
}
